import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router"
import { AlertCircle, Car01, MarkerPin01, Phone, RefreshCw01, XCircle } from "@untitledui/icons"
import { publicConsumerApi, type PublicShopProfile } from "@/lib/api/public-consumer"
import { resolveIntakeConfig } from "@/lib/intake/intake-config"
import type { ApiEstimate, ApiTeam } from "@/lib/api/types"
import { ShopHeader } from "@/components/estimates/consumer-intake-estimate/shop-chrome"
import { RepairTimeline } from "@/components/estimates/consumer-intake-estimate/repair-timeline"
import { FinalCostCard } from "@/components/estimates/consumer-intake-estimate/final-cost-card"

// Public repair-tracking page — /track/:trackToken. The SMS deep-link target.
// No login. Resolves the opaque token → estimate, derives the shop's branding
// from the estimate's team, and shows the consumer where their car is.
export default function ConsumerTrackPage() {
  const { trackToken } = useParams<{ trackToken: string }>()
  const [estimate, setEstimate] = useState<ApiEstimate | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")
  const [advancing, setAdvancing] = useState(false)
  // MOCK-ONLY demo control — lets a reviewer walk the repair through to
  // Completed (and the final-cost state) without a real shop acting.
  const isMock = import.meta.env.MODE === "mock"

  const load = useCallback(
    async (mode: "initial" | "silent") => {
      if (!trackToken) return
      if (mode === "initial") setState("loading")
      try {
        const e = await publicConsumerApi.getEstimate(trackToken)
        setEstimate(e)
        setState("ready")
      } catch {
        if (mode === "initial") setState("error")
      }
    },
    [trackToken],
  )

  // Initial load + light auto-refresh so a left-open page reflects shop updates.
  useEffect(() => {
    void load("initial")
    const id = setInterval(() => void load("silent"), 8000)
    return () => clearInterval(id)
  }, [load])

  const handleAdvance = async () => {
    if (!trackToken) return
    setAdvancing(true)
    try {
      const e = await publicConsumerApi.devAdvance(trackToken)
      setEstimate(e)
    } finally {
      setAdvancing(false)
    }
  }

  if (state === "loading") {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-gradient-to-b from-zinc-50 to-white">
        <span className="size-6 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-700" />
      </div>
    )
  }

  if (state === "error" || !estimate) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-gradient-to-b from-zinc-50 to-white px-6">
        <div className="max-w-sm text-center">
          <div className="mx-auto grid size-12 place-items-center rounded-full bg-amber-50">
            <AlertCircle className="size-6 text-amber-600" aria-hidden />
          </div>
          <h1 className="mt-4 text-[20px] font-bold tracking-tight text-zinc-900">
            We couldn’t find that repair
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
            This tracking link may have expired. Check the latest link from your
            shop, or give them a call.
          </p>
        </div>
      </div>
    )
  }

  const shop = shopFromTeam(estimate.dealership ?? estimate.bodyshop)
  const accent = shop.branding.primaryColor
  const vehicle =
    [estimate.vehicle?.year, estimate.vehicle?.make, estimate.vehicle?.model]
      .filter(Boolean)
      .join(" ") || "Your vehicle"
  const appointment = (estimate.metadata as Record<string, unknown> | null)
    ?.appointment as
    | { type: string; date: string; time: string }
    | undefined
  const declined = estimate.status === "Declined"

  return (
    <div className="relative min-h-[100dvh] bg-gradient-to-b from-zinc-50 to-white">
      <div className="relative mx-auto flex min-h-[100dvh] max-w-[560px] flex-col gap-5 px-4 pb-12 pt-5 md:px-6">
        <ShopHeader shop={shop} subtitle="Repair status" />

        {/* Vehicle headline */}
        <div className="flex items-center gap-3.5 rounded-3xl border border-zinc-200/70 bg-white p-5 shadow-sm">
          <div
            className="grid size-12 shrink-0 place-items-center rounded-2xl text-white"
            style={{ backgroundColor: accent }}
          >
            <Car01 className="size-6" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-zinc-500">
              Your repair
            </p>
            <p className="truncate text-[17px] font-semibold leading-tight text-zinc-900">
              {vehicle}
            </p>
          </div>
        </div>

        {declined ? (
          <div className="rounded-3xl border border-zinc-200/70 bg-white p-6 text-center shadow-sm">
            <div className="mx-auto grid size-12 place-items-center rounded-full bg-zinc-100">
              <XCircle className="size-6 text-zinc-400" aria-hidden />
            </div>
            <h2 className="mt-4 text-[18px] font-bold text-zinc-900">
              This estimate was closed
            </h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-zinc-500">
              No appointment is scheduled. If you still need the work done, reach
              out to {shop.name} and we’ll pick it back up.
            </p>
          </div>
        ) : (
          <>
            <RepairTimeline estimate={estimate} accent={accent} />

            {appointment && (
              <div className="rounded-2xl border border-zinc-200/70 bg-white px-4 py-4 shadow-sm">
                <p className="text-[13px] font-semibold text-zinc-500">
                  {appointment.type === "inspection" ? "Inspection" : "Drop-off"}
                </p>
                <p className="mt-1 text-[15px] font-semibold text-zinc-900">
                  {formatApptDate(appointment.date)} · {formatClock(appointment.time)}
                </p>
              </div>
            )}

            <FinalCostCard estimate={estimate} shop={shop} />
          </>
        )}

        {/* MOCK-ONLY demo control — advance the repair to see each state. */}
        {isMock && !declined && estimate.status !== "Completed" && (
          <button
            type="button"
            onClick={handleAdvance}
            disabled={advancing}
            className="mx-auto inline-flex items-center gap-1.5 rounded-full border border-dashed border-zinc-300 px-3 py-1.5 text-[13px] font-semibold text-zinc-500 transition-colors hover:text-zinc-700 disabled:opacity-50"
          >
            <RefreshCw01 className={advancing ? "size-3.5 animate-spin" : "size-3.5"} aria-hidden />
            Demo: advance repair status
          </button>
        )}

        {/* Contact footer */}
        <div className="mt-2 space-y-1 text-center text-[13px] text-zinc-500">
          {shop.phoneNumber && (
            <p className="flex items-center justify-center gap-1.5">
              <Phone className="size-3.5" aria-hidden />
              {shop.phoneNumber}
            </p>
          )}
          {(shop.street || shop.city) && (
            <p className="flex items-center justify-center gap-1.5">
              <MarkerPin01 className="size-3.5" aria-hidden />
              {[shop.street, shop.city, shop.state].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// Build a PublicShopProfile-shaped object from the estimate's team so the
// tracking page can reuse the shop chrome. Branding lives in team metadata
// (seeded for the body shop); falls back to emerald + derived initials.
function shopFromTeam(team: ApiTeam | undefined): PublicShopProfile {
  const meta = (team?.metadata ?? {}) as Record<string, unknown>
  const branding = (meta.branding ?? {}) as Record<string, unknown>
  const name = team?.name ?? "Your shop"
  return {
    id: team?.id ?? 0,
    name,
    type: team?.type ?? "Bodyshop",
    street: team?.street ?? null,
    city: team?.city ?? null,
    state: team?.state ?? null,
    zipCode: team?.zipCode ?? null,
    phoneNumber: team?.phoneNumber ?? null,
    branding: {
      primaryColor:
        typeof branding.primaryColor === "string" ? branding.primaryColor : "#059669",
      logoUrl: typeof branding.logoUrl === "string" ? branding.logoUrl : null,
      logoInitials:
        typeof branding.logoInitials === "string"
          ? branding.logoInitials
          : initialsOf(name),
      hours: typeof branding.hours === "string" ? branding.hours : "Mon-Fri 8AM-6PM",
    },
    intake: resolveIntakeConfig(meta.intake),
  }
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "SHOP"
  )
}

function formatApptDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
  })
}

function formatClock(time: string): string {
  const [h, m] = time.split(":").map(Number)
  const period = h >= 12 ? "PM" : "AM"
  const hour = h % 12 || 12
  return `${hour}:${String(m).padStart(2, "0")} ${period}`
}
